import type { FC, ForwardedRef } from 'react';
import { forwardRef, memo, useCallback, useImperativeHandle, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import type { GraphModalInstance } from '../../components/GraphModal/GraphModal';
import { GraphModal } from '../../components/GraphModal/GraphModal';
import { ReactComponent as IconVote } from '../../icons/icon-vote-active-inline.svg';
import type { Author } from '../../types/api';
import { NoSSR } from '../../utils/ssr';
import type { VoteData } from '../api/thread';
import type { VoteResp } from '../Vote/vote-model';
import { VoteContent } from '../Vote/VoteContent';
import { BottomActionButton } from './BottomActionButton';

const messages = defineMessages({ vote: { defaultMessage: 'Vote', id: 'Y6dihM' } });

export type VoterInstance = {
  voteOnAnswer: (author: Author | undefined) => void;
};

interface Props {
  currentVote?: VoteData;
  url: string;
  onVote?: (data: VoteResp) => void;
  ref?: ForwardedRef<VoterInstance>;
  className?: string;
}

export const VoteBottomAction: FC<Props> = memo(
  forwardRef(function VoteBottomAction(props, ref) {
    const { currentVote, className, ...others } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const repliedAuthorRef = useRef<Author | null>(null);

    const lazyContent = useCallback(
      (controller: GraphModalInstance) => (
        <VoteContent
          controller={controller}
          currentVote={currentVote as VoteData}
          repliedAuthor={repliedAuthorRef.current}
          {...others}
        />
      ),
      [currentVote, others, repliedAuthorRef],
    );
    const { formatMessage } = useIntl();

    useImperativeHandle(
      ref,
      () => ({
        voteOnAnswer: repliedAuthor => {
          const { current } = containerRef;

          if (currentVote || !current) return;
          repliedAuthorRef.current = repliedAuthor || null;

          current.click();
        },
      }),
      [containerRef, repliedAuthorRef, currentVote],
    );

    const onHide = useCallback(() => {
      repliedAuthorRef.current = null;
    }, []);

    return (
      <NoSSR>
        {/* Wrap in span to avoid a tippy warning */}
        <span>
          <GraphModal
            placement="bottom-end"
            theme="light-border"
            className="tippy-no-padding w-auto"
            lazyContent={lazyContent}
            onHide={onHide}
            maxWidth="none"
          >
            <BottomActionButton
              ref={containerRef}
              className={twMerge(currentVote && '!text-primary-base', className)}
              icon={<IconVote className="w-3 h-3 mb-[3px]" />}
              label={formatMessage(messages.vote)}
            />
          </GraphModal>
        </span>
      </NoSSR>
    );
  }),
);
